import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useRecordings, useRecordingsSummary } from "../api/recordings";
import { errorMessage } from "../api/client";
import RecordingGrid from "../components/RecordingGrid";
import BulkReviewBar from "../components/BulkReviewBar";
import SortControl, { type SortKey } from "../components/SortControl";
import UploaderFilter, { UNKNOWN_UPLOADER, type UploaderOption } from "../components/UploaderFilter";
import { REVIEWABLE_STATUSES } from "../lib/grade";

// A pill is a server-side view (GET /api/recordings?view=<key>): "Processing" groups the
// in-flight statuses; "Gate failed" cuts across statuses — these recordings have no value as
// delivered. Filtering, sorting and counting happen on the api so the page can paginate:
// loading the whole archive fired one thumbnail request per card and emptied the api's DB
// pool (2026-09-23), and silently hid everything past the first 200 recordings.
const FILTERS: { label: string; key: string; tone?: string }[] = [
  { label: "All", key: "all" },
  { label: "Pending", key: "pending_review" },
  { label: "Processing", key: "processing" },
  { label: "Approved", key: "approved" },
  { label: "Rejected", key: "rejected" },
  { label: "Fraud", key: "fraud" },
  { label: "Failed", key: "failed" },
  { label: "Gate failed", key: "gate_failed", tone: "bad" },
];

const PAGE_SIZE = 30;

/** Recordings library: the full archive, filterable by status and uploader. */
export default function RecordingsPage() {
  // The active pill lives in the URL (?f=pending_review) so the review queue is deep-linkable.
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get("f") ?? "all";
  const active = FILTERS.some((f) => f.key === requested) ? requested : "all";
  // 1-based page, also in the URL so "back" from a recording returns to the same page
  const page = Math.max(1, Number.parseInt(searchParams.get("p") ?? "1", 10) || 1);
  const setView = (key: string, nextPage = 1) => {
    const next: Record<string, string> = {};
    if (key !== "all") next.f = key;
    if (nextPage > 1) next.p = String(nextPage);
    setSearchParams(next, { replace: true });
  };
  const setActive = (key: string) => setView(key);
  const setPage = (n: number) => {
    setView(active, n);
    window.scrollTo({ top: 0 });
  };
  const [uploader, setUploaderState] = useState(""); // uploader uid, "" = all
  const [sort, setSortState] = useState<SortKey>("newest");
  // a new filter or order starts over from the first page
  const setUploader = (uid: string) => {
    setUploaderState(uid);
    setPage(1);
  };
  const setSort = (s: SortKey) => {
    setSortState(s);
    setPage(1);
  };
  // batch review: off by default so a stray click can never decide a recording
  const [picking, setPicking] = useState(false);
  const [selected, setSelected] = useState<Set<number>>(new Set());

  // cut parents (superseded) are excluded by every view: their processed outputs were
  // purged and the derived segments replace them. Reachable by direct URL only.
  const { data, isLoading, error, isFetching } = useRecordings({
    view: active,
    uploader: uploader || undefined,
    sort,
    limit: PAGE_SIZE,
    offset: (page - 1) * PAGE_SIZE,
  });
  const summary = useRecordingsSummary();

  const visible = data?.items ?? [];
  const total = data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const counts = summary.data?.counts ?? {};

  // the view can shrink under us (a batch review empties the last Pending page): land on the
  // last page that still has rows instead of an empty grid
  useEffect(() => {
    if (data && page > pageCount) setView(active, pageCount);
  }, [data, page, pageCount, active]);

  const uploaders = useMemo<UploaderOption[]>(() => {
    const options = [...(summary.data?.uploaders ?? [])];
    if (summary.data?.has_unknown_uploader) options.push({ uid: UNKNOWN_UPLOADER, name: "Unknown" });
    return options;
  }, [summary.data]);

  // only recordings that already reached a decidable status can be batch-reviewed
  const pickableIds = useMemo(
    () => visible.filter((r) => REVIEWABLE_STATUSES.has(r.status)).map((r) => r.id),
    [visible],
  );
  // derived, not stored: ids that scroll out of the filter (or were just decided) drop out
  // of the selection on their own.
  const activeSelection = useMemo(() => {
    const pickable = new Set(pickableIds);
    return new Set([...selected].filter((id) => pickable.has(id)));
  }, [selected, pickableIds]);

  const toggleSelect = (id: number) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  return (
    <div className="recordings-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Recordings</h1>
          <p className="page-sub">
            {data ? `${total} recording${total === 1 ? "" : "s"}` : "Loading…"}

            {isFetching && !isLoading ? " · refreshing" : ""}
          </p>
        </div>
        <div className="page-head-actions">
          <button
            className={picking ? "btn small primary" : "btn small"}
            onClick={() => {
              setPicking((v) => !v);
              setSelected(new Set());
            }}
          >
            {picking ? "Done selecting" : "Select"}
          </button>
          <SortControl sort={sort} onChange={setSort} />
        </div>
      </div>

      <div className="queue-toolbar">
        <div className="filter-pills">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              className={[
                "pill",
                active === f.key ? "active" : "",
                f.tone === "bad" && (counts[f.key] ?? 0) > 0 ? "pill-bad" : "",
              ]
                .filter(Boolean)
                .join(" ")}
              onClick={() => setActive(f.key)}
            >
              {f.label}
              <span className="pill-count">{counts[f.key] ?? 0}</span>
            </button>
          ))}
        </div>
        <UploaderFilter options={uploaders} value={uploader} onChange={setUploader} />
      </div>

      {picking && (
        <BulkReviewBar
          selectedIds={[...activeSelection]}
          visibleIds={pickableIds}
          onSelectAll={() => setSelected(new Set(pickableIds))}
          onClear={() => setSelected(new Set())}
        />
      )}

      {error && <p className="error">Failed to load recordings: {errorMessage(error)}</p>}

      <RecordingGrid
        items={visible}
        isLoading={isLoading}
        selectable={picking}
        selectedIds={activeSelection}
        canSelect={(r) => REVIEWABLE_STATUSES.has(r.status)}
        onToggleSelect={toggleSelect}
        emptyText={
          active === "pending_review"
            ? "Nothing to review."
            : active === "gate_failed"
              ? "No recording is failing the QC gate."
              : "No recordings here."
        }
        emptyHint={
          active === "pending_review"
            ? "Recordings land here once processing finishes."
            : active === "gate_failed"
              ? "Gate failures show up here across every status — each one needs a human to understand why."
              : "Adjust the filters, or scan storage from the Processing page."
        }
      />

      {total > PAGE_SIZE && (
        <div className="pager">
          <button className="btn small" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            ← Previous
          </button>
          <span className="muted small">
            {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total} · page{" "}
            {page} of {pageCount}
          </span>
          <button
            className="btn small"
            disabled={page >= pageCount}
            onClick={() => setPage(page + 1)}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
